import * as React from 'react'
import styled from 'styled-components'
import ReactSelect from 'react-select'
import I18n from '../../core/i18n'
import httpClient from '../../core/httpClient'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import { Button, Select, InputLabel } from 'components/atoms'
import TourSkillSingleSelect from 'components/molecules/TourSkill/TourSkillSingleSelect'
import DefaultTemplateGroups from 'components/molecules/DefaultTemplateGroups'
import { postService } from 'core/services'

declare var window: interfaces.IWindow

interface IOtherGroup {
  id: number
  post_id: number
  slug: string
  name: string
  short_name: string
  bokun_product_code: string
}

interface ISelectablePost {
  id: number
  bokun_product_code: string
  short_name: string
  name: string
}

interface IProps {
  post: interfaces.IPost
  sound_out_templates: any[]
  formal_request_templates: any[]
  tour_memo_templates: interfaces.ITourMemoTemplate[]
  tour_skills: any[]
  agent_ranks: interfaces.IAgentRank[]
  show_other_groups: IOtherGroup[]
  selectable_posts: ISelectablePost[]
}

const MAX_OTHER_GROUPS = 20

const buildGuideMeetingTimeOffsetOptions = () => [
  { value: '', label: I18n.t('generic.guide_meeting_time_offset_unset') },
  ...Array.from({ length: 19 }, (_, i) => {
    const minutes = i * 5
    return {
      value: String(minutes),
      label: I18n.t('generic.guide_meeting_time_offset_minutes_before', { minutes }),
    }
  }),
]

export const PostSettings: React.FC<IProps> = props => {
  const [isTourSkillSubmitting, setIsTourSkillSubmitting] = React.useState(false)
  const [isGuideMeetingTimeSubmitting, setIsGuideMeetingTimeSubmitting] = React.useState(false)
  const {
    post,
    sound_out_templates,
    formal_request_templates,
    tour_memo_templates,
    tour_skills,
    agent_ranks,
  } = props
  const [postTourSkill, setPostTourSkill] = React.useState<interfaces.IPostTourSkill>(
    post.post_tour_skill
  )
  const [guideMeetingTimeOffset, setGuideMeetingTimeOffset] = React.useState<number | null>(
    post.guide_meeting_time_offset ?? null
  )

  // Other Groups
  const [otherGroups, setOtherGroups] = React.useState<IOtherGroup[]>(props.show_other_groups || [])
  const [selectedPost, setSelectedPost] = React.useState<any>(null)
  const [isOtherGroupSubmitting, setIsOtherGroupSubmitting] = React.useState(false)

  // 選択肢のoptions生成
  const postOptions = React.useMemo(() => {
    const existingIds = new Set(otherGroups.map(g => g.post_id))
    existingIds.add(post.id) // 自分自身を除外
    return (props.selectable_posts || [])
      .filter((p: ISelectablePost) => !existingIds.has(p.id))
      .map((p: ISelectablePost) => ({
        value: String(p.id),
        label: `[${p.bokun_product_code || '-'}] ${p.short_name || ''} | ${p.name || ''}`,
      }))
  }, [props.selectable_posts, otherGroups, post.id])

  // 検索用キャッシュマップ
  const postCacheMap = React.useMemo(() => {
    const cacheMap = new Map()
    ;(props.selectable_posts || []).forEach((p: ISelectablePost) => {
      cacheMap.set(String(p.id), {
        short_name: p.short_name?.toLowerCase() || '',
        product_code: p.bokun_product_code?.toLowerCase() || '',
        name: p.name?.toLowerCase() || '',
      })
    })
    return cacheMap
  }, [props.selectable_posts])

  // カスタムフィルター（複数キーワード対応）
  const customFilterOption = React.useCallback(
    (option: any, searchText: string) => {
      if (!searchText) {
        return true
      }
      const target = postCacheMap.get(option.value)
      if (!target) {
        return false
      }
      const keywords = searchText
        .toLowerCase()
        .split(/[\s　]+/)
        .filter(k => k.trim() !== '')
      return keywords.every(
        q =>
          target.short_name.includes(q) ||
          target.product_code.includes(q) ||
          target.name.includes(q)
      )
    },
    [postCacheMap]
  )

  const handleAddOtherGroup = React.useCallback(async () => {
    if (!selectedPost) {
      return
    }
    if (otherGroups.length >= MAX_OTHER_GROUPS) {
      window.flashMessages.addMessage({
        text: `Other Groups の上限は ${MAX_OTHER_GROUPS} 件です`,
        type: 'error',
      })
      return
    }
    setIsOtherGroupSubmitting(true)
    try {
      const { data } = await httpClient.post(`/agent/api/posts/${post.id}/show_other_groups`, {
        related_post_id: Number(selectedPost.value),
      })
      setOtherGroups(data.show_other_groups)
      setSelectedPost(null)
      window.flashMessages.addMessage({ text: data.flush.message, type: data.flush.type })
    } catch (e) {
      const message = e.response?.data?.errors || 'エラーが発生しました'
      window.flashMessages.addMessage({ text: message, type: 'error' })
    } finally {
      setIsOtherGroupSubmitting(false)
    }
  }, [selectedPost, otherGroups, post.id])

  const handleRemoveOtherGroup = React.useCallback(
    async (recordId: number) => {
      if (!confirm('この Other Group を削除しますか？')) {
        return
      }
      setIsOtherGroupSubmitting(true)
      try {
        const { data } = await httpClient.delete(
          `/agent/api/posts/${post.id}/show_other_groups/${recordId}`
        )
        setOtherGroups(data.show_other_groups)
        window.flashMessages.addMessage({ text: data.flush.message, type: data.flush.type })
      } catch (e) {
        const message = e.response?.data?.errors || 'エラーが発生しました'
        window.flashMessages.addMessage({ text: message, type: 'error' })
      } finally {
        setIsOtherGroupSubmitting(false)
      }
    },
    [post.id]
  )

  const submitTourSkill = async (nextPostTourSkill: interfaces.IPostTourSkill) => {
    setIsTourSkillSubmitting(true)
    try {
      const { post: updatedPost, flush } = await postService.updatePostTourSkill(post.id, {
        post_tour_skill_attributes: {
          id: nextPostTourSkill?.id,
          tour_skill_id: nextPostTourSkill?.tour_skill_id,
          level: nextPostTourSkill?.level || '',
          _destroy: !nextPostTourSkill?.tour_skill_id,
        },
      })
      setPostTourSkill(updatedPost.post_tour_skill)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (e) {
      const message = e.response?.data?.errors || 'エラーが発生しました'
      window.flashMessages.addMessage({ text: message, type: 'error' })
    } finally {
      setIsTourSkillSubmitting(false)
    }
  }

  const submitGuideMeetingTime = async (nextGuideMeetingTimeOffset: number | null) => {
    setIsGuideMeetingTimeSubmitting(true)
    try {
      const { post: updatedPost, flush } = await postService.updatePostOperationSetting(post.id, {
        guide_meeting_time_offset: nextGuideMeetingTimeOffset,
      })
      setGuideMeetingTimeOffset(updatedPost.guide_meeting_time_offset ?? null)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (e) {
      const message = e.response?.data?.errors || 'エラーが発生しました'
      window.flashMessages.addMessage({ text: message, type: 'error' })
    } finally {
      setIsGuideMeetingTimeSubmitting(false)
    }
  }

  return (
    <Wrapper>
      <h2 className="Editor_Title">
        {I18n.t('generic.setting')} - {post.short_name || ''}
        {post.bokun_product_code ? ` (${post.bokun_product_code})` : ''}
      </h2>
      <div className="FormItems">
        <div className="FormItem templates tour_memo_templates">
          <InputLabel label={I18n.t('generic.sound_out_template')} />
          <TemplateGroups>
            <DefaultTemplateGroups
              resource="sound_out_templates"
              postId={post.id}
              templates={sound_out_templates}
              specialRanks={agent_ranks || []}
            />
            <TemplateFooter>
              <a
                href={`/agent/sound_out_templates/new?post_id=${post.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button small={true}>{I18n.t('generic.create')}</Button>
              </a>
            </TemplateFooter>
          </TemplateGroups>
        </div>
        <div className="FormItem templates tour_memo_templates">
          <InputLabel label={I18n.t('generic.formal_request_templates')} />
          <TemplateGroups>
            <DefaultTemplateGroups
              resource="formal_request_templates"
              postId={post.id}
              templates={formal_request_templates}
              specialRanks={agent_ranks || []}
            />
            <TemplateFooter>
              <a
                href={`/agent/formal_request_templates/new?post_id=${post.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button small={true}>{I18n.t('generic.create')}</Button>
              </a>
            </TemplateFooter>
          </TemplateGroups>
        </div>
        <div className="FormItem templates tour_memo_templates">
          <InputLabel label={I18n.t('generic.tour_memo_template')} />
          <TemplateGroups>
            <DefaultTemplateGroups
              resource="tour_memo_templates"
              postId={post.id}
              templates={tour_memo_templates}
            />
            <TemplateFooter>
              <a
                href={`/agent/tour_memo_templates/new?post_id=${post.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button small={true}>{I18n.t('generic.create')}</Button>
              </a>
            </TemplateFooter>
          </TemplateGroups>
        </div>
        <div className="FormItem tour_skill">
          <InputLabel label={I18n.t('generic.tour_skills')} />
          <TourSkillSingleSelect
            className="tourSkillSelect"
            options={tour_skills.map(skill => ({ value: String(skill.id), label: skill.name }))}
            value={postTourSkill?.tour_skill_id ? String(postTourSkill.tour_skill_id) : null}
            onChange={value => {
              const next = { ...postTourSkill, tour_skill_id: value ? Number(value) : null }
              submitTourSkill(next)
            }}
            placeholder={I18n.t('generic.select_skill')}
            isClearable={true}
            isDisabled={isTourSkillSubmitting}
          />
          <Select
            required={false}
            name="level"
            disabled={isTourSkillSubmitting}
            options={[{ value: '', label: I18n.t('generic.select_level') }].concat(
              Object.keys(constants.SKILL_LEVELS).map(key => ({
                value: key,
                label: constants.SKILL_LEVELS[key],
              }))
            )}
            value={postTourSkill?.tour_skill_id ? String(postTourSkill.level) : ''}
            onChangeHandler={e => {
              const next = { ...postTourSkill, level: e.target.value }
              submitTourSkill(next)
            }}
          />
        </div>
        <div className="FormItem guide_meeting_time_offset_setting">
          <InputLabel label={I18n.t('generic.guide_meeting_time_offset')} />
          <Select
            required={false}
            name="guide_meeting_time_offset"
            disabled={isGuideMeetingTimeSubmitting}
            options={buildGuideMeetingTimeOffsetOptions()}
            value={guideMeetingTimeOffset == null ? '' : String(guideMeetingTimeOffset)}
            onChangeHandler={e => {
              const next = e.target.value === '' ? null : Number(e.target.value)
              submitGuideMeetingTime(next)
            }}
          />
        </div>

        {/* Other Groups セクション */}
        <div className="FormItem other_groups">
          <InputLabel label="Other Groups" />
          <div className="other_groups_content">
            <div className="other_groups_selector">
              <ReactSelect
                placeholder="ツアーを検索（商品コード / short_name / ツアー名）"
                options={postOptions}
                isMulti={false}
                filterOption={customFilterOption}
                onChange={setSelectedPost}
                value={selectedPost}
                isDisabled={isOtherGroupSubmitting || otherGroups.length >= MAX_OTHER_GROUPS}
              />
              <Button
                small={true}
                primary={true}
                disabled={!selectedPost || isOtherGroupSubmitting}
                handleClick={handleAddOtherGroup}
              >
                追加
              </Button>
            </div>
            <p className="other_groups_count">
              {otherGroups.length} / {MAX_OTHER_GROUPS}
            </p>
            {otherGroups.length > 0 && (
              <table className="other_groups_table">
                <thead>
                  <tr>
                    <th>Product Code</th>
                    <th>Short Name</th>
                    <th>Name</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {otherGroups.map(group => (
                    <tr key={group.id}>
                      <td>
                        <a href={`/agent/posts/${group.slug}/edit?active=settings`}>
                          {group.bokun_product_code || '-'}
                        </a>
                      </td>
                      <td>
                        <a href={`/agent/posts/${group.slug}/edit?active=settings`}>
                          {group.short_name || '-'}
                        </a>
                      </td>
                      <td>
                        <a href={`/agent/posts/${group.slug}/edit?active=settings`}>
                          {group.name || '-'}
                        </a>
                      </td>
                      <td>
                        <Button
                          small={true}
                          disabled={isOtherGroupSubmitting}
                          handleClick={() => handleRemoveOtherGroup(group.id)}
                        >
                          削除
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

const TemplateGroups = styled.div`
  flex: 1;
  width: 100%;
  max-width: 680px;
  min-width: 0;
`

const TemplateFooter = styled.div`
  padding-top: 10px;
`

const Wrapper = styled.div`
  .FormItems {
    padding: 20px;

    .FormItem {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;

      div:first-child {
        width: 200px;
      }
    }
    .templates {
      a {
        text-decoration: underline;
        color: ${constants.COLORS.primary};
      }
    }
    .tour_skill {
      .tourSkillSelect {
        width: 100%;
        max-width: 400px;
        flex-grow: 1;
        div:first-child {
          width: auto;
        }
      }
    }
    .tour_memo_templates {
      align-items: flex-start;

      div:first-child {
        width: auto;
      }

      > div:first-child {
        width: 200px;
        flex-shrink: 0;
      }
    }
    .other_groups {
      display: block;

      > div:first-child {
        width: 200px;
      }

      .other_groups_content {
        width: 100%;
        margin-top: 8px;

        div:first-child {
          width: auto;
        }
      }
      .other_groups_selector {
        display: flex;
        align-items: center;
        gap: 10px;

        > div:first-child {
          flex: 1;
          width: auto;
        }
      }
      .other_groups_count {
        margin-top: 4px;
        font-size: 12px;
        color: ${constants.COLORS.subText};
      }
      .other_groups_table {
        width: 100%;
        margin-top: 12px;
        border-collapse: collapse;
        font-size: 13px;

        th,
        td {
          padding: 6px 8px;
          border-bottom: 1px solid ${constants.COLORS.divider};
          text-align: left;
        }
        th {
          font-weight: bold;
          color: ${constants.COLORS.subText};
        }
        td a {
          text-decoration: underline;
          color: ${constants.COLORS.primary};
        }
      }
    }
  }
`

export default PostSettings
